/**
 * @vuelto-surface
 * mobile: ok
 * desktop: ok
 * strategy: unified
 */
import React, { useCallback, useEffect, useMemo } from 'react';
import {
  RefreshControl,
  ScrollView,
  Text,
  TouchableOpacity,
  View,
} from 'react-native';
import { useNavigation } from '@react-navigation/native';
import { ensureTicketFonts } from '../../shared/ticket/ensureTicketFonts'
import { PaperSheet } from '../../shared/ticket/PaperSheet'
import { TicketAlertStrip } from '../../shared/ticket/TicketAlertStrip'
import { TicketKpiStrip } from '../../shared/ticket/TicketKpiStrip'
import type { TicketKpiItem } from '../../shared/ticket/TicketKpiStrip'
import { TicketListRow } from '../../shared/ticket/TicketListRow'
import type { TicketListRowGroup } from '../../shared/ticket/TicketListRow'
import { ticketColors } from '../../shared/theme/ticketColors';
import { ShellContentFrame } from '../../shared/layout/ShellContentFrame'
import { operationalTabListHostStyle } from '../../shared/layout/operationalTabListHost'
import { tabBarListContentStyle } from '../../shared/layout/tabBarListContentStyle'
import { useTabWorkspaceLayout } from '../../shared/layout/useContentLayout';
import { ModuleEmptyState } from '../../shared/ui/ModuleEmptyState';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { useOnlineOrdersScreen, ORDER_FILTER_LABELS, ORDER_FILTER_KEYS } from './useOnlineOrdersScreen';
import { useOrdersRealtime } from '../hooks/useOrdersRealtime';
import {
  ORDER_STATUS_LABELS,
  formatOrderDisplayCodeLabel,
  isPreparingDelayed,
  type CustomerOrderRow,
} from '../domain/orderTypes';
import { OrdersCashClosedBanner } from '../components/OrdersCashClosedBanner';
import { HomeBusyBanner } from '../../onboarding/components/HomeBusyBanner';
import { useNavVisibilityContext } from '../../shell/navigation/useNavVisibilityContext';
import { useSessionStore } from '../../shell/session/store/useSessionStore';
import { useModulePermission } from '../../shell/session/hooks/useModulePermission';
import { navigateInShell } from '../../shell/navigation/rootNavigation';
import { styles } from './OnlineOrdersScreen.styles';
import { useCapabilityGate } from '../../shell/session/hooks/useCapabilityGate';
import { formatMoney } from '../../shared/format/public-api';

function timeLabel(iso: string): string {
  const d = new Date(iso);
  return d.toLocaleTimeString('es-AR', { hour: '2-digit', minute: '2-digit', hour12: false });
}

/** Map order status → TicketListRow status tone. */
function statusToneFor(order: CustomerOrderRow): 'ok' | 'warn' | 'bad' | 'muted' {
  if (isPreparingDelayed(order)) return 'bad';
  switch (order.status) {
    case 'pending':
      return 'warn';
    case 'ready':
      return 'ok';
    case 'cancelled':
      return 'bad';
    default:
      return 'muted';
  }
}

export const OnlineOrdersScreen = () => {
  const navigation = useNavigation<any>();
  const { orders, refreshing, initialLoading, onRefresh, filter, setFilter, filterCounts, orgId, branchId } =
    useOnlineOrdersScreen();
  const { publicStoreFeaturesAllowed } = useNavVisibilityContext();
  const canViewOrders = useModulePermission('Orders', 'view');
  const deliveryGate = useCapabilityGate('delivery_fulfillment', {
    title: 'Pedidos online no disponibles',
    subtitle: 'Este rubro no tiene entregas ni pedidos online.',
  });
  const currentBranchId = useSessionStore((s) => s.currentBranchId);
  const activeBranchId = branchId ?? currentBranchId;
  const insets = useSafeAreaInsets();
  const { layoutWide } = useTabWorkspaceLayout();

  useEffect(() => {
    ensureTicketFonts();
  }, []);

  useOrdersRealtime(orgId, onRefresh, activeBranchId);

  const handlePressOrder = useCallback(
    (orderId: string) => {
      navigation.navigate('OnlineOrderDetail', { orderId });
    },
    [navigation],
  );

  const openHistory = useCallback(() => {
    navigateInShell(navigation, 'OrdersHistory');
  }, [navigation]);

  const onlineRelevant = useMemo(() => {
    if (publicStoreFeaturesAllowed) return true;
    return orders.length > 0;
  }, [orders, publicStoreFeaturesAllowed]);

  // ── KPI tiles (4) — reemplazan los chips de filtro ──
  const kpiItems = useMemo<TicketKpiItem[]>(() => {
    return ORDER_FILTER_KEYS.map((key) => {
      const count = filterCounts[key];
      const tone: TicketKpiItem['tone'] =
        key === 'pending' && count > 0
          ? 'warn'
          : key === 'ready' && count > 0
            ? 'ok'
            : 'default';
      return {
        label: ORDER_FILTER_LABELS[key],
        value: String(count),
        tone,
        onPress: () => setFilter(key),
        selected: filter === key,
        accessibilityLabel: `${ORDER_FILTER_LABELS[key]}: ${count}`,
        testID: `orders.filter.${key}`,
      };
    });
  }, [filterCounts, filter, setFilter]);

  // ── Alert strip: caja cerrada > demorados (uno a la vez) ──
  const delayedCount = useMemo(() => {
    return orders.filter((o) => isPreparingDelayed(o)).length;
  }, [orders]);

  const showCashClosedStrip = onlineRelevant; // OrdersCashClosedBanner maneja la lógica de sesión

  const alertStrip = useMemo(() => {
    if (!showCashClosedStrip) return null;
    return (
      <OrdersCashClosedBanner
        variant="inbox"
        onlineRelevant={onlineRelevant}
      />
    );
  }, [showCashClosedStrip, onlineRelevant]);

  const delayedStrip = useMemo(() => {
    if (delayedCount <= 0) return null;
    if (filter === 'active') return null; // ya está viendo el filtro
    return (
      <TicketAlertStrip
        title={`Hay ${delayedCount} pedido${delayedCount > 1 ? 's' : ''} demorado${delayedCount > 1 ? 's' : ''}`}
        subtitle="Verlos en En preparación"
        tone="bad"
        onPress={() => setFilter('active')}
        testID="orders.alert.delayed"
        accessibilityLabel="Filtrar pedidos demorados"
      />
    );
  }, [delayedCount, filter, setFilter]);

  // ── Lista de filas Square ──
  const listRows = useMemo(() => {
    if (orders.length === 0) return null;
    return orders.map((item, index) => {
      const group: TicketListRowGroup =
        index === orders.length - 1 ? 'groupedLast' : 'grouped';
      const delayed = isPreparingDelayed(item);
      const awaitingCustomer = item.resolution_status === 'pending_customer';
      const fulfillmentChip = item.pickup_type === 'pickup' ? 'Retiro' : 'Envío';
      const statusLabel = delayed ? 'Demorado' : ORDER_STATUS_LABELS[item.status];
      const code = formatOrderDisplayCodeLabel(item.display_code, item.id);
      return (
        <TicketListRow
          key={item.id}
          title={item.customer_name}
          subtitle={code}
          meta={`${timeLabel(item.created_at)} · ${fulfillmentChip}`}
          value={formatMoney(item.total_amount)}
          statusLabel={statusLabel}
          statusTone={statusToneFor(item)}
          chipLabel={awaitingCustomer ? 'Esperando al cliente' : null}
          chipTone={awaitingCustomer ? 'warn' : 'default'}
          group={group}
          onPress={() => handlePressOrder(item.id)}
          accessibilityLabel={`Pedido ${code} de ${item.customer_name}, ${statusLabel}`}
          testID={`orders.card.${item.id}`}
        />
      );
    });
  }, [orders, handlePressOrder]);

  // ── Empty state ──
  const emptyState = useMemo(() => {
    if (initialLoading || refreshing) {
      return (
        <ModuleEmptyState
          loading
          loadingText="Cargando pedidos…"
          testID="orders.empty"
        />
      );
    }
    const isPendingEmpty = filter === 'pending';
    const title = isPendingEmpty
      ? 'Acá llegan los pedidos'
      : `No hay pedidos ${ORDER_FILTER_LABELS[filter].toLowerCase()}`;
    const subtitle = isPendingEmpty
      ? 'Cuando alguien pida en la web, aparece acá para que lo aceptes o rechaces.'
      : 'Los que están en curso. Los cerrados están en Historial. Las ventas, en Ventas.';
    return (
      <View style={styles.emptyProto} testID="orders.empty">
        <Text style={styles.emptyProtoTitle}>{title}</Text>
        <Text style={styles.emptyProtoMeta}>{subtitle}</Text>
        <ModuleEmptyState
          loading={false}
          variant="nest"
          kicker="Siguiente paso"
          title={isPendingEmpty ? 'Esperá el primer pedido' : 'Mirá el historial'}
          subtitle={
            isPendingEmpty
              ? 'Los pedidos de la web caen acá solos.'
              : 'Los pedidos cerrados se ven en Historial.'
          }
          primaryAction={
            isPendingEmpty
              ? undefined
              : { label: 'Ver historial', onPress: openHistory, testID: 'orders.empty.history' }
          }
        />
      </View>
    );
  }, [filter, initialLoading, openHistory, refreshing]);

  // ── Content style (padding + bottom inset) ──
  const listContentStyle = useMemo(
    () =>
      tabBarListContentStyle({
        bottomInset: insets.bottom,
        baseContentStyle: styles.listContent,
        emptyGrowStyle: orders.length === 0 ? styles.listContentEmptyGrow : null,
      }),
    [insets.bottom, orders.length],
  );

  // ── Historial CTA (mobile full-width; desktop va en el header del wrapper) ──
  const mobileHistoryBtn = (
    <TouchableOpacity
      style={styles.mobileHistoryBtn}
      onPress={openHistory}
      accessibilityRole="button"
      accessibilityLabel="Historial de pedidos"
      testID="orders.filter.history"
    >
      <Text style={styles.mobileHistoryBtnText}>Historial</Text>
    </TouchableOpacity>
  );

  if (!deliveryGate.allowed) {
    return <ShellContentFrame style={styles.container}>{deliveryGate.blocked}</ShellContentFrame>;
  }

  if (!canViewOrders) {
    return (
      <ShellContentFrame style={styles.container}>
        <ModuleEmptyState
          variant="nest"
          kicker="Pedidos"
          title="No podés ver Pedidos"
          subtitle="Pedile al dueño que te dé permiso."
          testID="orders.no-permission"
        />
      </ShellContentFrame>
    );
  }

  return (
    <ShellContentFrame style={styles.container}>
      <View style={operationalTabListHostStyle.host}>
        <View style={{ flex: 1, minHeight: 0 }}>
          <ScrollView
            style={styles.list}
            contentContainerStyle={listContentStyle}
            refreshControl={
              <RefreshControl
                refreshing={refreshing}
                onRefresh={onRefresh}
                colors={[ticketColors.ink2]}
                tintColor={ticketColors.ink2}
              />
            }
            keyboardShouldPersistTaps="handled"
            showsVerticalScrollIndicator={false}
          >
            <View style={styles.stack}>
              {/* Status tienda + Historial + KPI papers */}
              <View style={styles.headerStack}>
                <HomeBusyBanner />
                {layoutWide ? null : mobileHistoryBtn}
                <PaperSheet testID="orders.kpi.sheet">
                  <TicketKpiStrip
                    items={kpiItems}
                    layout={layoutWide ? 'desk' : 'mobile'}
                    variant="soft"
                  />
                </PaperSheet>
              </View>

              {/* Alert strips (uno a la vez: caja cerrada > demorados) */}
              {alertStrip}
              {!alertStrip ? delayedStrip : null}

              {/* Body paper: lista Square o empty */}
              <PaperSheet style={styles.listSheet} testID="orders.list.sheet">
                {listRows}
                {orders.length === 0 ? emptyState : null}
              </PaperSheet>
            </View>
          </ScrollView>
        </View>
      </View>
    </ShellContentFrame>
  );
};
